import { Link } from "react-router-dom";
import { canAccess, minPlanLabelFor } from "../lib/planGate.js";
import { usePlan } from "../lib/usePlan.js";
import { useIsRoot } from "../lib/useIsRoot.js";
import ProBadge from "./ProBadge.jsx";

/**
 * Route-level guard for plan-gated pages. When the active workspace's plan does
 * not include `feature`, the real page is never rendered — instead we show an
 * upgrade wall. This masks Professional-only features from Standard users
 * (the backend is still the authority; this is the UI half of the gate).
 *
 * Usage:
 *   <Route path="/servers" element={<PlanGate feature="servers"><Servers /></PlanGate>} />
 */
export default function PlanGate({ feature, children }) {
  const plan = usePlan();
  const isRoot = useIsRoot();
  if (canAccess(feature, plan)) {
    return children;
  }

  const planLabel = minPlanLabelFor(feature) || "Professional";

  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <div className="max-w-md rounded-xl border border-gray-200 bg-white p-8 text-center shadow-sm">
        <div className="mb-4 flex justify-center">
          <ProBadge className="h-6 w-auto" />
        </div>
        <h2 className="text-lg font-semibold text-gray-900">
          A {planLabel} feature
        </h2>
        <p className="mt-2 text-sm text-gray-500">
          This feature is available on the {planLabel} plan.
          {isRoot
            ? " Upgrade your workspace to unlock it."
            : " It's managed from the root workspace."}
        </p>
        {isRoot && (
          <Link
            to="/billing"
            className="mt-5 inline-block rounded-md bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700"
          >
            Upgrade to {planLabel}
          </Link>
        )}
      </div>
    </div>
  );
}
